import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Alert } from '@mui/material';
import { PlayArrow as ExecuteIcon } from '@mui/icons-material';
import {
  validateTemplateVariables,
  validateTemplateVariablesEnhanced,
  cleanInputData,
} from '../utils/executionUtils';
import {
  InputDataSection,
  SystemPromptSection,
  TemplatePreviewSection,
  ExecutionSettingsSection,
  ExecutionActionsSection,
} from './ExecutionFormComponents';
import { OUTPUT_FORMATS } from '../../../config/constants';

const ExecutionForm = ({
  template,
  executionData,
  setExecutionData,
  showSettings,
  setShowSettings,
  executing,
  templateId,
  onExecuteTemplate,
  onExecuteTemplateSimplified,
  onResetForm,
  onEditTemplate,
  parseTemplateVariables,
  handleInputDataChange,
  addInputField,
  removeInputField,
  handleExecutionSettingChange,
  selectedVersion,
}) => {
  const [localError, setLocalError] = useState(null);

  const handleExecute = async e => {
    // Prevent form submission and page refresh
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      setLocalError(null);

      const validation = validateTemplateVariablesEnhanced(
        template,
        executionData
      );
      if (!validation.isValid) {
        const missingCount = validation.missingVariables.length;
        const missingList = validation.missingVariables
          .map(v => v.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
          .join(', ');

        const errorMessage =
          missingCount === 1
            ? `Please fill in the required field: ${missingList}`
            : `Please fill in the following required fields: ${missingList}`;

        setLocalError(errorMessage);
        return;
      }

      const cleanInputDataValue = cleanInputData(executionData.input_data);

      // Note: template_id is in the URL path, not the request body
      // Use template's provider from llm_config or model_config, fallback to empty string if not available
      const templateProvider =
        template?.llm_config?.provider ||
        template?.model_config?.provider ||
        '';
      const templateModelName =
        template?.llm_config?.model_name ||
        template?.model_config?.model_name ||
        '';

      const payload = {
        input_data: cleanInputDataValue,
        system_prompt: executionData.system_prompt || template?.system_prompt,
        llm_config: {
          provider: templateProvider,
          model_name: templateModelName,
          temperature: executionData.execution_settings.temperature,
          max_tokens: executionData.execution_settings.max_tokens,
          top_p: executionData.execution_settings.top_p,
          frequency_penalty: executionData.execution_settings.frequency_penalty,
          presence_penalty: executionData.execution_settings.presence_penalty,
        },
        output_format: executionData.output_format,
        // Include version_number if a version is selected
        ...(selectedVersion?.version_number && {
          version_number: selectedVersion.version_number,
        }),
      };

      await onExecuteTemplate(payload);
      setLocalError(null); // Clear error on success
    } catch (error) {
      setLocalError(error.message || 'An error occurred during execution');
    }
  };

  const handleExecuteSimplified = async e => {
    // Prevent form submission and page refresh
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      setLocalError(null);

      const validation = validateTemplateVariables(template, executionData);
      if (!validation.isValid) {
        const missingCount = validation.missingVariables.length;
        const missingList = validation.missingVariables
          .map(v => v.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
          .join(', ');

        const errorMessage =
          missingCount === 1
            ? `Please fill in the required field: ${missingList}`
            : `Please fill in the following required fields: ${missingList}`;

        setLocalError(errorMessage);
        return;
      }

      const cleanInputDataValue = cleanInputData(executionData.input_data);

      // Use template's provider from llm_config or model_config, fallback to empty string if not available
      const templateProvider =
        template?.llm_config?.provider ||
        template?.model_config?.provider ||
        '';
      const templateModelName =
        template?.llm_config?.model_name ||
        template?.model_config?.model_name ||
        '';

      const payload = {
        input_data: cleanInputDataValue,
        llm_config: {
          provider: templateProvider,
          model_name: templateModelName,
          temperature: executionData.execution_settings.temperature,
          max_tokens: executionData.execution_settings.max_tokens,
          top_p: executionData.execution_settings.top_p,
          frequency_penalty: executionData.execution_settings.frequency_penalty,
          presence_penalty: executionData.execution_settings.presence_penalty,
        },
        // Include version_number if a version is selected
        ...(selectedVersion?.version_number && {
          version_number: selectedVersion.version_number,
        }),
      };

      await onExecuteTemplateSimplified(payload);
      setLocalError(null); // Clear error on success
    } catch (error) {
      setLocalError(error.message || 'An error occurred during execution');
    }
  };

  // Clear error when input data changes
  useEffect(() => {
    if (localError) {
      setLocalError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionData.input_data]);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <ExecuteIcon sx={{ mr: 1 }} />
        Execute Template
      </Typography>

      {/* Error Alert */}
      {localError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setLocalError(null)}
        >
          {localError}
        </Alert>
      )}

      {/* Input Data */}
      <InputDataSection
        template={template}
        executionData={executionData}
        setExecutionData={setExecutionData}
        handleInputDataChange={handleInputDataChange}
        addInputField={addInputField}
        removeInputField={removeInputField}
      />

      {/* System Prompt */}
      <SystemPromptSection
        executionData={executionData}
        setExecutionData={setExecutionData}
      />

      {/* Template Preview */}
      <TemplatePreviewSection
        template={template}
        executionData={executionData}
      />

      {/* Output Format - Read-only display from template */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Output Format
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            color: 'text.primary',
          }}
        >
          {(() => {
            const formatValue = executionData.output_format || 'text';
            const formatOption = OUTPUT_FORMATS.find(
              f => f.value === formatValue
            );
            return formatOption ? formatOption.label : 'Text Response';
          })()}
        </Typography>
      </Box>

      {/* Execution Settings */}
      <ExecutionSettingsSection
        executionData={executionData}
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        handleExecutionSettingChange={handleExecutionSettingChange}
      />

      {/* Execution Actions */}
      <ExecutionActionsSection
        executing={executing}
        templateId={templateId}
        onExecute={handleExecute}
        onExecuteSimplified={handleExecuteSimplified}
        onResetForm={onResetForm}
        onEditTemplate={onEditTemplate}
      />
    </Paper>
  );
};

export default ExecutionForm;
